import { createContext, useCallback, useContext, useRef, useState } from "react";

const WorkspaceModeContext = createContext({
  mode: "dashboard",
  setMode: () => {},
});

/**
 * Global workspace mode — "dashboard" or "ni2".
 * When "ni2" is active, the left navigation sidebar swaps into a
 * Recent Conversations navigator for the duration of the session.
 *
 * Also carries the "flashConv" primitive: while Ni² streams a response
 * and cites a specific case, reflection or paper, the conversation in
 * the ConversationsSidebar that hosts that reference briefly glows —
 * communicating that the answer is grounded in identifiable memory
 * rather than generated in isolation.
 */
export function WorkspaceModeProvider({ children }) {
  const [mode, setMode] = useState("dashboard");
  const [activeConversationId, setActiveConversationId] = useState("CV-2145");
  const [flashedConvId, setFlashedConvId] = useState(null);
  const timerRef = useRef(null);

  const flashConv = useCallback((id, ms = 800) => {
    if (!id) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    setFlashedConvId(id);
    timerRef.current = setTimeout(() => setFlashedConvId(null), ms);
  }, []);

  return (
    <WorkspaceModeContext.Provider
      value={{
        mode,
        setMode,
        activeConversationId,
        setActiveConversationId,
        flashedConvId,
        flashConv,
      }}
    >
      {children}
    </WorkspaceModeContext.Provider>
  );
}

export function useWorkspaceMode() {
  return useContext(WorkspaceModeContext);
}
